import classnames from 'classnames'
import React from 'react'

import { PageItems, TablePaginationProps } from '../../hooks/use-table-pagination'
import { Button, ButtonComponentProps } from '../button'
import { LabelWithDelayedSpinner } from '../label-with-delayed-spinner'

interface PaginationProps<TData, TButton extends React.ElementType>
  extends Omit<TablePaginationProps<TData>, 'visiblePages' | 'paginatedData' | 'pageCount'>,
    ButtonComponentProps {
  isLoading?: boolean
  visiblePages: Array<number | PageItems.gap>
  getPageItemButtonProps?: (page: number, pageItem: PageItems) => ButtonComponentProps<TButton>
  className?: string
}

export function Pagination<TData, TagType extends React.ElementType>({
  canGoBack,
  goBack,
  canGoForward,
  goForward,
  canGoToPage,
  goToPage,
  visiblePages,
  currentPage,
  isLoading = false,
  getPageItemButtonProps,
  className,
  ...buttonProps
}: PaginationProps<TData, TagType>) {
  const prevButtonProps = getPageItemButtonProps?.(currentPage - 1, PageItems.prev) as ButtonComponentProps<TagType>
  const nextButtonProps = getPageItemButtonProps?.(currentPage + 1, PageItems.next) as ButtonComponentProps<TagType>
  return (
    <div className={classnames('flex gap-1', className)}>
      <div className="flex w-full justify-end">
        <Button
          title="Go to previous page"
          onClick={goBack}
          rightContent="arrowBackward"
          theme="chipTransparent"
          size="small"
          disabled={!canGoBack}
          {...buttonProps}
          {...prevButtonProps}
          className={classnames(prevButtonProps?.className, {
            'pointer-events-none invisible opacity-0': !canGoBack,
          })}
        />
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {visiblePages.map((pageNumber, index) => {
          if (pageNumber === PageItems.gap) {
            return (
              <span key={`${pageNumber}-${index}`} className="text-gray-200 font-bold dark:text-gray-600">
                ⋯
              </span>
            )
          }

          const pageNumberButtonProps = getPageItemButtonProps?.(
            pageNumber,
            PageItems.pageNumber
          ) as ButtonComponentProps<TagType>
          const isActive = currentPage === pageNumber

          return (
            <Button
              key={pageNumber}
              title={`Go to page ${pageNumber}`}
              disabled={!canGoToPage(pageNumber)}
              onClick={goToPage ? () => goToPage(pageNumber) : undefined}
              active={isActive}
              theme="chipTransparent"
              size="small"
              {...buttonProps}
              {...pageNumberButtonProps}
              className={classnames(pageNumberButtonProps?.className, 'relative min-w-7')}
            >
              <LabelWithDelayedSpinner label={pageNumber} isLoading={isActive && isLoading} />
            </Button>
          )
        })}
      </div>
      <div className="w-full">
        <Button
          title="Go to next page"
          onClick={goForward}
          rightContent="arrowForward"
          theme="chipTransparent"
          size="small"
          {...buttonProps}
          {...nextButtonProps}
          className={classnames(nextButtonProps?.className, {
            'pointer-events-none invisible opacity-0': !canGoForward,
          })}
        />
      </div>
    </div>
  )
}
